import { getSymbol } from "../shared/symbols";
import { commonPtBR, symbolNamesPtBR } from "./locales/common.pt-BR";
import { gamePtBR } from "./locales/game.pt-BR";
import { collectionsPtBR } from "./locales/collections.pt-BR";
import { uiPtBR } from "./locales/ui.pt-BR";
import { isLocale, preferredLocale, type Locale } from "../shared/locale";

export type { Locale } from "../shared/locale";

const STORAGE_KEY = "pile-locale";
const translations: Readonly<Record<string, string>> = {
  ...commonPtBR,
  ...uiPtBR,
  ...gamePtBR,
  ...collectionsPtBR,
};
let activeLocale: Locale | undefined;

function setDocumentLanguage(locale: Locale): void {
  if (typeof document !== "undefined" && document.documentElement) {
    document.documentElement.lang = locale;
  }
}

/** Explicit choices win; otherwise use the server's country/language hint or browser languages. */
export function getLocale(): Locale {
  if (activeLocale === undefined) {
    const hint = typeof document !== "undefined" ? document.documentElement?.getAttribute?.("data-locale") : null;
    const automatic = isLocale(hint) ? hint : preferredLocale(
      typeof navigator === "undefined" ? [] : navigator.languages ?? [navigator.language],
    );
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      activeLocale = isLocale(stored) ? stored : automatic;
    } catch {
      activeLocale = automatic;
    }
    setDocumentLanguage(activeLocale);
  }
  return activeLocale;
}

export function setLocale(locale: Locale): void {
  // Guard runtime inputs as well as TypeScript consumers.
  if (!isLocale(locale)) return;
  activeLocale = locale;
  setDocumentLanguage(locale);
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // The current page can still change language when storage is blocked.
  }
}

/** Returns plain text. The caller owns HTML escaping for interpolated values. */
export function t(
  sourceFrench: string,
  vars?: Record<string, string | number>,
): string {
  const translated =
    getLocale() === "pt-BR" && Object.hasOwn(translations, sourceFrench)
      ? translations[sourceFrench]
      : sourceFrench;
  return translated.replace(
    /\{([a-zA-Z0-9_]+)\}/g,
    (placeholder, name: string) =>
      vars && Object.hasOwn(vars, name) ? String(vars[name]) : placeholder,
  );
}

export function symbolName(id: number): string {
  const symbol = getSymbol(id);
  return getLocale() === "pt-BR"
    ? (symbolNamesPtBR[symbol.id] ?? symbol.name)
    : symbol.name;
}

/** Server messages use the same canonical French keys as local text. */
export function serverMessage(message: string): string {
  return t(message);
}

export function intlNumber(
  value: number,
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(
    getLocale() === "pt-BR" ? "pt-BR" : "fr-FR",
    options,
  ).format(value);
}
